<template>
  <el-col :sm="12">
    <base-dialog
      :title="$t('employee.addEmployee')"
      :openDialog="openAddDialog"
    >
      <el-form
        :model="addEmployeeForm"
        :rules="validator"
        ref="addEmployeeForm"
        label-position="top"
        class="add-modal"
        @submit.native.prevent
      >
        <el-row :gutter="50" class="row-input">
          <el-col :xs="24" :sm="12">
            <el-form-item :label="$t('employee.name')" prop="name">
              <el-input
                class="input"
                v-model="addEmployeeForm.name"
                autocomplete="off"
              >
                <i
                  slot="suffix"
                  class="el-input__icon el-icon-circle-close el-input__clear"
                  @click="addEmployeeForm.name = ''"
                  v-if="addEmployeeForm.name"
                ></i>
              </el-input>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12">
            <el-form-item :label="$t('employee.email')" prop="email">
              <el-input
                class="input"
                v-model="addEmployeeForm.email"
                autocomplete="off"
              >
                <i
                  slot="suffix"
                  class="el-input__icon el-icon-circle-close el-input__clear"
                  @click="addEmployeeForm.email = ''"
                  v-if="addEmployeeForm.email"
                ></i>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="50" class="row-input">
          <el-col :xs="24" :sm="12">
            <el-form-item :label="$t('employee.phone')" prop="phone">
              <el-input
                class="input"
                autocomplete="off"
                @input="acceptNumber"
                v-model="addEmployeeForm.phone"
              >
                <i
                  slot="suffix"
                  class="el-input__icon el-icon-circle-close el-input__clear"
                  @click="addEmployeeForm.phone = ''"
                  v-if="addEmployeeForm.phone"
                ></i>
              </el-input>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12">
            <el-form-item :label="$t('employee.jobTitle')" prop="role">
              <el-input
                class="input"
                v-model="addEmployeeForm.role"
                autocomplete="off"
              >
                <i
                  slot="suffix"
                  class="el-input__icon el-icon-circle-close el-input__clear"
                  @click="addEmployeeForm.role = ''"
                  v-if="addEmployeeForm.role"
                ></i>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="50" class="row-input">
          <el-col :xs="24" :sm="12">
            <el-form-item :label="$t('employee.labelStore')" prop="storeId">
              <el-autocomplete
                class="input"
                ref="autocomplete"
                popper-class="popper-drop-up"
                :placeholder="$t('employee.storePlaceholder')"
                v-model="storeAutoComplete.label"
                :fetch-suggestions="remoteMethod"
                :popper-append-to-body="false"
                @select="handleSelect"
                @change="handleChange"
                @blur="handleBlur"
              >
                <i
                  slot="suffix"
                  class="el-input__icon el-icon-circle-close el-input__clear"
                  @click="handleAutocomplete"
                  v-if="storeAutoComplete.label"
                ></i>
              </el-autocomplete>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item>
          <span class="dialog-footer">
            <el-button
              class="btn btn--cancel"
              @click="handleCloseAddDialog('addEmployeeForm')"
            >
              {{ $t('employee.cancelBtn') }}
            </el-button>
            <el-button
              class="btn btn--submit"
              :disabled="isLoadingStore"
              @click="submitForm('addEmployeeForm')"
            >
              {{ $t('employee.confirmBtn') }}
            </el-button>
          </span>
        </el-form-item>
      </el-form>
    </base-dialog>
  </el-col>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { employeeService } from '@/services/employee.service';
import {
  convertHalfWidth,
  handleError,
  notifyMessage,
  redirectRouter,
} from '@/helper';
import { email, name, phone, store } from '@/helper/validate';
import { LIMIT, PAGE } from '@/constants/common';
import * as pageRouter from '@/constants/pageRouter';

const BaseDialog = () => import('@/components/Dialog');

export default {
  name: 'AddEmployeeDialog',
  props: {
    openAddDialog: Boolean,
  },
  components: { BaseDialog },
  data() {
    return {
      addEmployeeLoading: false,
      addEmployeeForm: {
        name: '',
        email: '',
        phone: '',
        role: '',
        storeId: null,
      },
      // Autocomplete
      storeAutoComplete: {
        label: '',
        options: [],
      },
      validator: {
        name: [{ validator: name, trigger: 'blur' }],
        phone: [{ validator: phone, trigger: 'blur' }],
        email: [{ validator: email, trigger: 'blur' }],
        storeId: [{ validator: store, trigger: 'blur' }],
      },
    };
  },
  computed: {
    ...mapGetters('store', ['isLoadingStore', 'store', 'error']),
    mapStoreData() {
      return this.store.data.map((item) => {
        return { storeId: item.id, value: item.name };
      });
    },
  },
  methods: {
    ...mapActions('employee', ['getEmployeeList']),
    ...mapActions('store', ['getStoreList']),

    submitForm(addEmployeeForm) {
      this.$refs[addEmployeeForm].validate(async (valid) => {
        if (valid) {
          this.addEmployeeLoading = true;
          return await employeeService
            .add({
              name:
                this.addEmployeeForm.name !== ''
                  ? this.addEmployeeForm.name.trim()
                  : undefined,
              phone: this.addEmployeeForm.phone.trim(),
              role: this.addEmployeeForm.role.trim(),
              storeId: this.addEmployeeForm.storeId,
              email: this.addEmployeeForm.email
                ? this.addEmployeeForm.email.trim()
                : null,
            })
            .then(() => {
              this.getEmployeeList({
                page: PAGE,
                limit: LIMIT,
              });
              this.$emit('handleCloseAddDialog');
              redirectRouter({
                path: pageRouter.EMPLOYEE,
                query: {
                  name: undefined,
                  page: undefined,
                },
              });
              this.$refs[addEmployeeForm].resetFields();
              this.storeAutoComplete.label = null;
              notifyMessage(this.$t('common.addSuccess'), 'success');
            })
            .catch((error) => {
              handleError(error);
            })
            .finally(() => {
              this.addEmployeeLoading = false;
            });
        }
      });
    },
    handleAutocomplete() {
      this.storeAutoComplete.label = '';
      this.addEmployeeForm.storeId = null;
    },

    handleCloseAddDialog(addEmployeeForm) {
      this.storeAutoComplete.label = null;
      this.$refs[addEmployeeForm].resetFields();
      this.$emit('handleCloseAddDialog');
    },

    /* Autocomplete */
    async remoteMethod(query, cb) {
      await this.getStoreList({
        freeText: query,
        onlyName: 1,
      });
      this.storeAutoComplete.options = this.mapStoreData || [];

      cb(this.storeAutoComplete.options);
    },

    handleSelect(item) {
      this.addEmployeeForm.storeId = item.storeId;
    },
    handleChange() {
      this.addEmployeeForm.storeId = null;
    },
    handleBlur() {
      if (!this.addEmployeeForm.storeId) {
        this.storeAutoComplete.label = null;
      }
    },
    acceptNumber(value) {
      this.addEmployeeForm.phone = convertHalfWidth(value);
    },
  },
  watch: {
    'addEmployeeForm.email'() {
      if (this.$refs['addEmployeeForm']) {
        this.$refs['addEmployeeForm'].clearValidate('email');
      }
    },
    'addEmployeeForm.name'() {
      if (this.$refs['addEmployeeForm']) {
        this.$refs['addEmployeeForm'].clearValidate('name');
      }
    },
    'addEmployeeForm.phone'() {
      if (this.$refs['addEmployeeForm']) {
        this.$refs['addEmployeeForm'].clearValidate('phone');
      }
    },
    'addEmployeeForm.role'() {
      if (this.$refs['addEmployeeForm']) {
        this.$refs['addEmployeeForm'].clearValidate('role');
      }
    },
    'addEmployeeForm.storeId'() {
      if (this.$refs['addEmployeeForm']) {
        this.$refs['addEmployeeForm'].clearValidate('storeId');
      }
    },
  },
};
</script>

<style lang="scss" scope>
.add-modal {
  .el-autocomplete {
    width: 100%;
  }

  .el-autocomplete-suggestion {
    margin: 0 !important;
  }
}
</style>
